import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Screen } from '../components/Screen';
import { BigButton } from '../components/BigButton';
import { useWorksheet } from '../context/WorksheetContext';
import { analyseWorksheet, isCloudVisionEnabled, VisionError } from '../lib/vision';
import { telemetry } from '../lib/telemetry';
import { useExamMode } from '../context/ExamModeContext';

// Step 2: confirm the photo, then go to the answer screen. AI mode is on by default
// (the user can turn it off in Settings, with the same opt-out treatment communicated
// during onboarding). We do not interrupt with an opt-in modal on every worksheet.
export function ReviewScreen() {
  const navigate = useNavigate();
  const { state, setAnalysis } = useWorksheet();
  const { active: examModeActive, logEvent } = useExamMode();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!state.image) {
      navigate('/app/capture', { replace: true });
    } else {
      telemetry.track('worksheet_review_shown', {});
    }
  }, [state.image, navigate]);

  if (!state.image) return null;

  async function start() {
    if (!state.image) return;
    setError(null);
    setBusy(true);

    try {
      // Exam Mode forces manual mode regardless of the AI toggle. The vision call
      // is never made; the candidate taps each answer slot themselves.
      const analysis = examModeActive
        ? { source: 'manual' as const, documentDetected: true, orientation: 'portrait' as const, questions: [] }
        : await analyseWorksheet(state.image);
      setAnalysis(analysis);
      telemetry.track('worksheet_processed', {
        mode: analysis.source,
        questions_detected: analysis.questions.length,
        exam_mode: examModeActive,
      });
      if (examModeActive) {
        logEvent('worksheet_captured', { questions_detected: 0, mode: 'manual' });
      }

      if (analysis.source === 'cloud' && !analysis.documentDetected) {
        // Server-side safeguarding has decided this is not a worksheet.
        setError('That does not look like a worksheet. Try a fresh photo.');
        setBusy(false);
        return;
      }

      navigate('/app/answer');
    } catch (e) {
      if (e instanceof VisionError) {
        setError(`We could not read the worksheet right now (${e.message}). Try manual mode.`);
      } else {
        setError('Something went wrong. Try manual mode for now.');
      }
      setBusy(false);
    }
  }

  const cloudOn = !examModeActive && isCloudVisionEnabled();

  return (
    <Screen>
      <div className="flex-1 flex flex-col gap-6 py-6">
        <header>
          <button
            onClick={() => navigate('/app/capture', { replace: true })}
            className="text-ink/60 text-sm hover:text-ink transition"
            aria-label="Back to capture"
          >
            ← Retake photo
          </button>
        </header>

        <div className="bg-white rounded-2xl border border-ink/10 p-3 shadow-sm">
          <img
            src={state.image}
            alt="Captured worksheet"
            className="max-h-[55vh] w-auto mx-auto rounded-xl"
          />
        </div>

        {error && (
          <div
            role="alert"
            className="bg-warm border border-amber-300 text-ink rounded-xl p-4 text-sm leading-relaxed"
          >
            {error}
          </div>
        )}

        {/* Tiny mode badge so users see what's running and how to change it. */}
        <p className="text-center text-xs text-ink/50">
          {cloudOn
            ? 'AI mode on. Change in Settings.'
            : 'Manual mode. Turn AI on in Settings for sharper question detection.'}
        </p>

        <div className="flex flex-col gap-3">
          <BigButton onClick={start} disabled={busy}>
            {busy ? 'Reading worksheet...' : 'Start answering'}
          </BigButton>
          <BigButton variant="secondary" onClick={() => navigate('/app/capture', { replace: true })}>
            Retake photo
          </BigButton>
        </div>
      </div>
    </Screen>
  );
}
